<template>
	<vue-final-modal v-slot="{ params, close }" v-bind="$attrs" classes="modal fade show" content-class="modal-dialog" :click-to-close="false">
		<form class="modal-content" role="form" @submit.prevent="onEdit(close)">
			<div class="modal-header py-2">
				<h1 class="modal-title fs-5">{{$t('header.exam-edit')}}</h1>
				<button type="button" class="btn-close" @click="$emit('cancel', close)" aria-label="Close"></button>
			</div>
			<div class="modal-body">
				<div class="text-center" v-if="loading">
					<loading-spinner
						:overlay="loading"
						:text="$t('text.loading-data')"
					></loading-spinner>
				</div>
				<div v-else>
					<div class="mb-3">
						<label for="select-lang" class="form-label">{{$t('text.exam-lang')}}:</label>
						<select id="select-lang" ref="lang" class="form-select" v-model="lang" :disabled="exam.ended" required>
							<option :value="lang.key" v-for="lang in appModel.examLangs" :key="lang.key">{{lang.name}}</option>
						</select>
					</div>
					<div class="mb-3">
						<label for="select-organization" class="form-label">
							{{$t('text.organization')}}:
							<font-awesome-icon :icon="['fas', 'spinner']" spin v-if="loadingOrganizations" class="ms-2" />
						</label>
						<select id="select-organization" class="form-select" v-model="organization_id" required :disabled="exam.ended || loadingOrganizations">
							<option :value="organization.id" v-for="organization in organizations" :key="organization.id">{{organization.name}}</option>
						</select>
					</div>
					<div class="mb-3">
						<label for="input-group" class="form-label">{{$t('text.group')}}:</label>
						<input id="input-group" ref="group_name" type="text" :placeholder="$t('text.group')" class="form-control" v-model="group_name" readonly disabled />
					</div>
					<div class="row mb-3">
						<div class="col-md-4">
							<label for="input-date" class="form-label">{{$t('text.exam-date')}}:</label>
							<input id="input-date" type="date" class="form-control text-center" v-model="date" required />
						</div>
						<div class="col-md-4">
							<label for="input-timer" class="form-label">{{$t('text.duration-in-minutes')}}:</label>
							<input id="input-timer" type="number" class="form-control text-center" min="1" max="1440" v-model.number="timer" @wheel="$event.target.blur()" :disabled="exam.ended" required />
						</div>
						<div class="col-md-4">
							<label for="input-questions-count" class="form-label">{{$t('text.questions-count')}}:</label>
							<input id="input-questions-count" type="number" class="form-control text-center" min="1" v-model="questions_count" :disabled="exam.ended" required />
						</div>
					</div>
					<div class="mb-3">
						<div class="form-check form-switch">
							<input class="form-check-input" type="checkbox" role="switch" id="flexSwitchIsShowResults" v-model="is_show_results" />
							<label class="form-check-label" for="flexSwitchIsShowResults">{{$t('text.exam-show-results')}}</label>
						</div>
					</div>
					<div>
						<label class="form-label">
							{{$t('text.disciplines')}} ({{checked_disciplines.filter(el => el.checked).length}}):
							<font-awesome-icon :icon="['fas', 'spinner']" spin v-if="disciplinesLoading" class="ms-2" />
						</label>
						<form role="form" class="mb-2" @submit.prevent="disciplinesPage = 1; loadDisciplines();">
							<div class="input-group">
								<input type="text" v-model="disciplinesSearch" class="form-control" :placeholder="$t('placeholder.search')" />
								<button type="submit" class="btn btn-outline-secondary" :disabled="disciplinesLoading" :content="$t('tooltip.btn.search')" v-tippy="{placement: 'top'}">
									<font-awesome-icon :icon="['fas', disciplinesLoading ? 'spinner' : 'magnifying-glass']" :spin="disciplinesLoading" />
								</button>
							</div>
						</form>
						<div v-if="!disciplinesLoading && checked_disciplines.length > 0 && disciplines.length > 0">
							<div v-for="discipline in disciplines" :key="discipline.id">
								<div class="form-check" v-if="getCheckedDiscipline(discipline.id)">
									<label class="form-check-label">
										<input class="form-check-input" type="checkbox" v-model="getCheckedDiscipline(discipline.id).checked" :disabled="exam.ended" />
										<span>{{discipline.name}}</span>
									</label>
								</div>
							</div>
						</div>
						<div class="alert alert-info mb-0" v-if="disciplines.length <= 0">{{$t('text.disciplines-list-for-select-empty')}}</div>
						<div class="text-center mt-2" v-if="getDisciplinesPagesCount() > 1">
							<nav aria-label="Page navigation">
								<ul class="pagination pagination-sm justify-content-center mb-0">
									<li class="page-item" :class="{'disabled': disciplinesPage == 1}">
										<a class="page-link" href="javascript://" @click="gotoDisciplinesPage(disciplinesPage - 1)" aria-label="Previous">
											<font-awesome-icon :icon="['fas', 'chevron-left']" />
										</a>
									</li>
									<li class="page-item" :class="{'active': disciplinesPage == 1}">
										<a class="page-link" href="javascript://" @click="gotoDisciplinesPage(1)">1</a>
									</li>
									<li class="page-item disabled" v-if="disciplinesPage - paginationAlpha > 2">
										<span class="page-link">...</span>
									</li>
									<li class="page-item" :class="{'active': disciplinesPage == i}" v-for="i in range(disciplinesPaginationA1, disciplinesPaginationA2)" :key="i" v-if="disciplinesPaginationA2 >= disciplinesPaginationA1">
										<a class="page-link" href="javascript://" @click="gotoDisciplinesPage(i)">{{i}}</a>
									</li>
									<li class="page-item disabled" v-if="disciplinesPage + paginationAlpha < getDisciplinesPagesCount() - 1">
										<span class="page-link">...</span>
									</li>
									<li class="page-item" :class="{'active': disciplinesPage == getDisciplinesPagesCount()}" v-if="getDisciplinesPagesCount() > 1">
										<a class="page-link" href="javascript://" @click="gotoDisciplinesPage(getDisciplinesPagesCount())">{{getDisciplinesPagesCount()}}</a>
									</li>
									<li class="page-item" :class="{'disabled': disciplinesPage == getDisciplinesPagesCount() || getDisciplinesPagesCount() == 0}">
										<a class="page-link" href="javascript://" @click="gotoDisciplinesPage(disciplinesPage + 1)" aria-label="Next">
											<font-awesome-icon :icon="['fas', 'chevron-right']" />
										</a>
									</li>
								</ul>
							</nav>
						</div>
					</div>
				</div>
				<div class="alert alert-warning alert-dismissible fade show mt-2 mb-0" v-if="error">
					{{error}}
					<button type="button" class="btn-close" aria-label="Close" @click="error = ''"></button>
				</div>
			</div>
			<div class="modal-footer p-1">
				<button type="submit" class="btn btn-primary" :disabled="loading">{{$t('btn.save-changes')}}</button>
				<button type="button" class="btn btn-outline-secondary" @click="$emit('cancel', close)">{{$t('btn.cancel')}}</button>
			</div>
		</form>
	</vue-final-modal>
</template>

<style lang="scss">

</style>

<script>
import lib from '@/lib';
//import autosize from 'autosize';
import _ from 'lodash';

import LoadingSpinner from '@/components/LoadingSpinner';

export default {
	mixins: lib.mixins,
	inheritAttrs: false,
	//emits: ['confirm', 'cancel']
	data: () => ({
		exam: {},
		
		organizations: [],
		
		lang: 'ru',
		organization_id: '',
		group_name: '',
		date: '',
		timer: 150,
		questions_count: 25,
        is_show_results: false,
		checked_disciplines: [],
		
		disciplines: [],
		disciplines_count: 0,
		
		disciplinesSearch: '',
		disciplinesPage: 1,
		disciplinesLimit: 10,
		disciplinesLoading: true,
		
		error: '',
		loading: false,
		loadingOrganizations: false,
		
		paginationAlpha: 3,
		
		loadedLang: false,
	}),
	watch: {
		lang(val, oldVal){
			if(this.loadedLang){
				this.loadCheckedDisciplines();
				this.loadDisciplines();
			}
			this.loadedLang = true;
		},
	},
	computed: {
		disciplinesPaginationA1(){
			return Math.max(this.disciplinesPage - this.paginationAlpha, 2);
		},
		disciplinesPaginationA2(){
			return Math.min(this.disciplinesPage + this.paginationAlpha, this.getDisciplinesPagesCount() - 1);
		},
	},
	methods: {
		range: lib.range,
		formatDate: lib.formatDate,
		formatDateUtc: lib.formatDateUtc,
		
		getCheckedDiscipline(discipline_id){
			return this.checked_disciplines.find(el => el.id == discipline_id);
		},
		
		// получение списка учебных заведений
		async loadOrganizations(){
			this.loadingOrganizations = true;
			this.error = '';
			let ret = await this.appModel.doGetOrganizations();
			if(ret.success){
				this.organizations = ret.list;
			} else {
				this.error = ret.message;
			}
			this.loadingOrganizations = false;
		},
		
		// получение списка всех возможных дисциплин
		async loadCheckedDisciplines(){
			this.disciplinesLoading = true;
			this.error = '';
			const ret = await this.appModel.doGetDisciplinesByLang(this.lang);
			if(ret.success){
				this.checked_disciplines = ret.list.map(el => {
					el.checked = false;
					return el;
				});
			} else {
				this.error = ret.message;
			}
			this.disciplinesLoading = false;
		},
		
		// получение списка дисциплин
		async loadDisciplines(){
			this.disciplinesLoading = true;
			this.error = '';
			const params = {
				search: this.disciplinesSearch,
				start: (this.disciplinesPage - 1) * this.disciplinesLimit,
				limit: this.disciplinesLimit,
			};
			const ret = await this.appModel.doGetDisciplinesByLang(this.lang, params);
			if(ret.success){
				this.disciplines = ret.list;
				this.disciplines_count = ret.count;
			} else {
				this.error = ret.message;
			}
			this.disciplinesLoading = false;
		},
		
		async onEdit(close){
			this.loading = true;
			this.error = '';
			let ret = await this.appModel.doEditExam(this.$vfm.modals[0].params._value.exam.id, {
				exam_lang: this.lang,
				organization_id: this.organization_id,
				date: this.formatDate(this.date, 'YYYY-MM-DD'),
				timer: this.timer,
				questions_count: this.questions_count,
				is_show_results: this.is_show_results,
				disciplines_id: _.cloneDeep(this.checked_disciplines.filter(el => el.checked)).map(el => el.id),
			});
			if(ret.success){
				this.$emit('edit', close);
			} else {
				this.error = ret.message;
			}
			this.loading = false;
		},
		
		gotoDisciplinesPage(page){
			if(page >= 1 && page <= this.getDisciplinesPagesCount() && page != this.page){
				this.disciplinesPage = page;
				this.loadDisciplines();
			}
		},
		// получение количества страниц
		getDisciplinesPagesCount(){
			return Math.ceil(this.disciplines_count / this.disciplinesLimit);
		},
	},
	async mounted(){
		const exam = this.$vfm.modals[0].params._value.exam;
		this.exam = exam;
		
		this.lang = exam.lang;
		if(this.lang == 'ru') this.loadedLang = true;
		
		this.group_name = exam.group_name;
		this.date = this.formatDate(exam.date, 'YYYY-MM-DD');
		this.timer = exam.timer;
		this.questions_count = exam.questions_count;
		this.is_show_results = !!exam.is_show_results;
		
		await this.loadOrganizations();
		this.organization_id = exam.organization_id;
		
		await this.loadCheckedDisciplines();
		await this.loadDisciplines();
		const disciplines_id = _.cloneDeep(exam.disciplines).map(el => el.id);
		for(const discipline of this.checked_disciplines){
			discipline.checked = disciplines_id.includes(discipline.id);
		}
	},
	components: {
		LoadingSpinner,
	},
}
</script>